import React, { ReactElement, useContext } from 'react';
import { t } from 'i18next';
import { Market } from '@oneaudi/avp-models';
import styled from 'styled-components';
import { screen } from '../../common/breakpoints';
import { HorizontalSpacing, Spacing, TypoScale, VerticalSpacing } from '../../common/spacings';
import { NeutralColors, PetrolColors } from '../../common/colors';
import { GiveawayContext } from '../../services/GiveawayContextService';
import { getCupraConfiguratorWebsite } from '../../i18n/websites';
import { isFromMyPrivateArea, saveToMyGarageUrl } from './saveToMyGarage';
import { SocialPopover } from '../socialPopover/socialPopover';

const StyledLowBarContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  @media screen and ${screen.l} {
    display: flex;
    flex-direction: row-reverse;
    text-align: center;
    gap: ${Spacing.L};
    justify-content: right;
    padding-left: ${HorizontalSpacing.L};
  }

  @media screen and ${screen.xl} {
    padding-left: ${HorizontalSpacing.XL};
  }
`;

const StyledPrimaryButton = styled.button`
  cursor: pointer;
  padding: 1rem 0;
  color: ${NeutralColors.Black};
  border: 1px solid ${NeutralColors.Black};
  background-color: ${NeutralColors.White};
  font-family: 'CupraRegular', sans-serif;
  font-size: ${TypoScale.L};
  line-height: 1.2;
  white-space: nowrap;
  margin-bottom: ${VerticalSpacing.S};
  width: 280px;
  height: 56px;
  position: relative;

  @media ${screen.l} {
    margin-bottom: 0;
  }

  &::before,
  &::after {
    content: '';
    display: inline-block;
    padding: ${Spacing.S};
    flex-basis: 1em;
  }

  &:hover {
    border: 1px solid ${PetrolColors.Petrol100};
    transition: all 0.3s ease;
    background-color: ${NeutralColors.Black};
    color: ${NeutralColors.White};
  }
`;

const StyledSecondaryButton = styled(StyledPrimaryButton)`
  background-color: ${NeutralColors.Black};
  color: ${NeutralColors.White};

  &:hover {
    background-color: ${NeutralColors.White};
    color: ${NeutralColors.Black};
  }
`;

const StyledInformationContainer = styled.div`
  width: inherit;
  padding-top: spacings .$spacing-s;
  text-align: center;

  @media screen and ${screen.l} {
    padding-top: 0;
  }
`;

const StyledModelContainer = styled.div`
  display: flex;
  flex-direction: column;

  @media screen and ${screen.l} {
    display: none;
  }

  span:first-of-type {
    font-weight: bold;
  }
`;

const StyledTextContainer = styled.div`
  span:first-of-type {
    font-weight: bold;
    margin-right: 5px;
  }
`;

export const Lowbar = (): ReactElement => {
  const { oscarConfigId, market, marketingCode, id, dealerId, locale, carModel } =
    useContext(GiveawayContext);

  const isValidDealerId = dealerId && /[A-Z]{3}(\d{5})[S]?/.test(dealerId);
  const visibleSaveButton = isValidDealerId && market === Market.ESP && !isFromMyPrivateArea();

  const configurationRedirect = () => {
    const url = getCupraConfiguratorWebsite(oscarConfigId, market);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const saveToMyGarageRedirect = () => {
    const url = saveToMyGarageUrl(market, locale, id, marketingCode, dealerId!);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <StyledLowBarContainer>
      <StyledSecondaryButton
        onClick={() => configurationRedirect()}
        data-testid="configurator-link"
      >
        {t('sideBar.adjust')}
      </StyledSecondaryButton>
      {visibleSaveButton && (
        <StyledPrimaryButton onClick={() => saveToMyGarageRedirect()} data-testid="save-link">
          {t('sideBar.save')}
        </StyledPrimaryButton>
      )}
      <StyledInformationContainer>
        {carModel && (
          <StyledModelContainer>
            <span>{t('header.model')}:</span>
            <span>{carModel}</span>
          </StyledModelContainer>
        )}
        <StyledTextContainer>
          <span>{t('sideBar.marketingCode')}:</span>
          <span>{marketingCode}</span>
        </StyledTextContainer>
      </StyledInformationContainer>
      <SocialPopover />
    </StyledLowBarContainer>
  );
};
